import { Slot } from '@radix-ui/react-slot';
import * as Stepperize from '@stepperize/react';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../utils/cn/cn';
import { Button } from '../Button/Button';

/**
 * This is a shadcn integration of the Stepperize library.
 *
 * For documentation see https://stepperize.vercel.app/docs/react/shadcn/api
 */

const classForNavigationList = cva('flex md:gap-1', {
  variants: {
    variant: {
      horizontal: 'flex-row items-center justify-between',
      vertical: 'flex-col',
      circle: 'flex-row items-center justify-between',
    },
  },
});

const classForSeparator = cva(
  [
    'bg-color-grey-light',
    'data-[state=completed]:bg-color-primary data-disabled:opacity-50',
    'transition-all duration-300 ease-in-out',
  ],
  {
    variants: {
      orientation: {
        horizontal: 'mt-2 h-0.5 flex-1 self-start',
        vertical: 'h-full w-0.5',
      },
      labelOrientation: {
        vertical: 'absolute top-2.5 right-[calc(-50%+20px)] left-[calc(50%+30px)] block shrink-0',
      },
    },
  },
);

const StepperContext = React.createContext<Stepper.ConfigProps | null>(null);

const useStepperProvider = (): Stepper.ConfigProps => {
  const context = React.useContext(StepperContext);
  if (!context) {
    throw new Error('useStepper must be used within a StepperProvider.');
  }
  return context;
};

const Title = ({
  children,
  className,
  asChild,
  ...props
}: React.ComponentProps<'span'> & { asChild?: boolean }) => {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp className={cn('text-size-body-sm cursor-pointer', className)} {...props}>
      {children}
    </Comp>
  );
};

const Description = ({
  children,
  className,
  asChild,
  ...props
}: React.ComponentProps<'p'> & { asChild?: boolean }) => {
  const Comp = asChild ? Slot : 'p';

  return (
    <Comp className={className} {...props}>
      {children}
    </Comp>
  );
};

const StepperSeparator = ({
  orientation,
  isLast,
  labelOrientation,
  state,
  disabled,
}: {
  isLast: boolean;
  state: string;
  disabled?: boolean;
} & VariantProps<typeof classForSeparator>) => {
  if (isLast) {
    return null;
  }
  return (
    <div
      data-orientation={orientation}
      data-state={state}
      data-disabled={disabled}
      role="separator"
      tabIndex={-1}
      className={classForSeparator({ orientation, labelOrientation })}
    />
  );
};

const defineStepper = <Steps extends readonly Stepperize.Step[]>(
  stepsProp: Steps,
): Stepper.DefineProps<Steps> => {
  const { Provider, useStepper, steps, get } = Stepperize.defineStepper(stepsProp);

  const getStepState = (currentIndex: number, stepIndex: number) => {
    if (currentIndex === stepIndex) {
      return 'active';
    }
    if (currentIndex > stepIndex) {
      return 'completed';
    }
    return 'inactive';
  };

  const extractChildren = (children: React.ReactNode) => {
    const childrenArray = React.Children.toArray(children);
    const map = new Map<string, React.ReactNode>();

    for (const child of childrenArray) {
      if (React.isValidElement(child)) {
        if (child.type === Title) {
          map.set('title', child);
        } else if (child.type === Description) {
          map.set('description', child);
        } else {
          map.set('panel', child);
        }
      }
    }

    return map;
  };

  const useStepChildren = (children: React.ReactNode) =>
    React.useMemo(() => extractChildren(children), [children]);

  const onStepKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    nextStep: Stepperize.Step,
    prevStep: Stepperize.Step,
  ) => {
    const { key } = e;
    const directions = {
      next: ['ArrowRight', 'ArrowDown'],
      prev: ['ArrowLeft', 'ArrowUp'],
    };

    if (directions.next.includes(key) || directions.prev.includes(key)) {
      const direction = directions.next.includes(key) ? 'next' : 'prev';
      const step = direction === 'next' ? nextStep : prevStep;

      if (!step) {
        return;
      }

      const stepElement = document.getElementById(`step-${step.id}`);
      if (!stepElement) {
        return;
      }

      const isActive = stepElement.parentElement?.getAttribute('data-state') !== 'inactive';
      if (isActive || direction === 'prev') {
        stepElement.focus();
      }
    }
  };

  function scrolltoStepperPanel(node: HTMLDivElement | null, tracking?: boolean) {
    if (tracking) {
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  const StepperContainer = ({
    children,
    className,
    ...props
  }: Omit<React.ComponentProps<'div'>, 'children'> & {
    children:
      | React.ReactNode
      | ((props: { stepper: Stepperize.Stepper<Steps> }) => React.ReactNode);
  }) => {
    const stepper = useStepper();

    return (
      <div className={cn('w-full', className)} {...props}>
        {typeof children === 'function' ? children({ stepper }) : children}
      </div>
    );
  };

  return {
    useStepper,
    Stepper: {
      Provider: ({
        variant = 'horizontal',
        labelOrientation = 'horizontal',
        tracking = false,
        children,
        className,
        defaultStep = steps[0]?.id,
        defaultData,
        ...props
      }) => (
        <StepperContext.Provider
          value={React.useMemo(
            () => ({ variant, labelOrientation, tracking }),
            [variant, labelOrientation, tracking],
          )}
        >
          <Provider defaultStep={defaultStep} defaultData={defaultData}>
            <StepperContainer className={className} {...props}>
              {children}
            </StepperContainer>
          </Provider>
        </StepperContext.Provider>
      ),
      Navigation: ({ children, 'aria-label': ariaLabel = 'Stepper Navigation', ...props }) => {
        const { variant } = useStepperProvider();
        return (
          <nav aria-label={ariaLabel} {...props}>
            <ol className={classForNavigationList({ variant })}>{children}</ol>
          </nav>
        );
      },
      Step: ({ children, className, icon, ...props }) => {
        const { variant, labelOrientation } = useStepperProvider();
        const stepper = useStepper();

        const stepIndex = steps.findIndex((step) => step.id === props.of);
        const step = get(props.of) ?? steps[stepIndex] ?? steps[0];
        const currentIndex = stepper.index;

        const isLast = steps[steps.length - 1]?.id === props.of;
        const isActive = stepper.id === props.of;

        const dataState = getStepState(currentIndex, stepIndex);
        const childMap = useStepChildren(children);

        const title = childMap.get('title');
        const description = childMap.get('description');
        const panel = childMap.get('panel');

        return (
          <>
            <li
              className="group peer relative flex items-center gap-1 data-[label-orientation=vertical]:flex-col data-[label-orientation=vertical]:justify-center data-[variant=vertical]:flex-row"
              data-variant={variant}
              data-label-orientation={labelOrientation}
              data-state={dataState}
              data-disabled={props.disabled}
            >
              <Button
                id={`step-${step.id}`}
                className="data-[state=inactive]:opacity-35"
                data-state={dataState}
                variant="icon-flat"
                size="medium"
                aria-controls={`step-panel-${props.of}`}
                aria-current={isActive ? 'step' : undefined}
                aria-posinset={stepIndex + 1}
                aria-setsize={stepper.count}
                aria-selected={isActive}
                onKeyDown={(e) => {
                  onStepKeyDown(e, steps[stepIndex + 1], steps[stepIndex - 1]);
                }}
                onClick={props.onClick}
              >
                {icon ? (
                  <>
                    <span className="hidden md:inline">{icon}</span>
                    <span
                      data-state={dataState}
                      className="bg-color-primary data-[state=inactive]:bg-color-grey-dark size-2 rounded-full md:hidden"
                    />
                  </>
                ) : (
                  stepIndex + 1
                )}
              </Button>
              <div
                className="data-[state=inactive]:text-color-grey-dark hidden flex-col items-start md:flex"
                data-state={dataState}
              >
                {title}
                {description}
              </div>
            </li>

            {variant === 'horizontal' && (
              <StepperSeparator
                orientation="horizontal"
                isLast={isLast}
                state={dataState}
                disabled={props.disabled}
              />
            )}

            {variant === 'vertical' && (
              <div className="flex gap-4">
                {!isLast && (
                  <div className="flex justify-center ps-[calc(var(--spacing)*4.5-1px)]">
                    <StepperSeparator
                      orientation="vertical"
                      isLast={isLast}
                      state={dataState}
                      disabled={props.disabled}
                    />
                  </div>
                )}
                <div className="my-3 flex-1 ps-4">{panel}</div>
              </div>
            )}
          </>
        );
      },
      Title,
      Description,
      Panel: ({ children, asChild, ...props }) => {
        const Comp = asChild ? Slot : 'div';
        const { tracking } = useStepperProvider();

        return (
          <Comp ref={(node) => scrolltoStepperPanel(node, tracking)} {...props}>
            {children}
          </Comp>
        );
      },
      Controls: ({ children, className, asChild, ...props }) => {
        const Comp = asChild ? Slot : 'div';
        return (
          <Comp className={cn('flex justify-end gap-4', className)} {...props}>
            {children}
          </Comp>
        );
      },
    },
  };
};

// This is copied from https://stepperize.vercel.app/docs/react/shadcn/installation
// I did not change the namespace to be as near as possible to the original library.
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Stepper {
  export type StepperVariant = 'horizontal' | 'vertical';
  export type StepperLabelOrientation = 'horizontal' | 'vertical';

  export type ConfigProps = {
    variant?: StepperVariant;
    labelOrientation?: StepperLabelOrientation;
    tracking?: boolean;
  };

  export type DefineProps<Steps extends readonly Stepperize.Step[]> = {
    useStepper: (props?: Stepperize.UseStepperOptions<Steps>) => Stepperize.Stepper<Steps>;
    Stepper: {
      Provider: (
        props: Omit<Stepperize.ProviderProps<Steps>, 'children'> &
          Omit<React.ComponentProps<'div'>, 'children'> &
          ConfigProps & {
            children:
              | React.ReactNode
              | ((props: { stepper: Stepperize.Stepper<Steps> }) => React.ReactNode);
          },
      ) => React.ReactElement;
      Navigation: (props: React.ComponentProps<'nav'>) => React.ReactElement;
      Step: (
        props: React.ComponentProps<'button'> & {
          of: Stepperize.Get.Id<Steps>;
          icon?: React.ReactNode;
        },
      ) => React.ReactElement;
      Title: (props: AsChildProps<'span'>) => React.ReactElement;
      Description: (props: AsChildProps<'p'>) => React.ReactElement;
      Panel: (props: AsChildProps<'div'>) => React.ReactElement;
      Controls: (props: AsChildProps<'div'>) => React.ReactElement;
    };
  };
}

type AsChildProps<T extends React.ElementType> = React.ComponentProps<T> & {
  asChild?: boolean;
};

export const Stepper = () => {
  throw new Error(
    'Stepper component is not meant to be instantiated directly. View stories for usage.',
  );
};

export { defineStepper };
